/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useIntl, FormattedMessage} from 'react-intl'

import {
    Flex,
    Heading,
    Button,
    Skeleton,
    Box,
    Text,
    VStack,
    HStack,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useDerivedProduct} from '../../hooks/use-derived-product'

// project components
import ImageGallery from '@salesforce/retail-react-app/app/components/image-gallery'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Skeleton as ImageGallerySkeleton} from '@salesforce/retail-react-app/app/components/image-gallery'
import {HideOnDesktop} from '@salesforce/retail-react-app/app/components/responsive'
import {useToast} from '../../hooks/use-toast'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import Swatch from '../../components/swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import {getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils'
import InformationAccordion from '../../pages/product-detail/partials/information-accordion/index'
import {
    addToCartButton,
    priceStyle,
    renderButtons,
    savingPriceStyle,
    salePriceStyle,
    titleStyle,
    wishlistButton,
    wishlistIcon,
    hideAfterEighthImgs,
    wishlistTooltip
} from '../../pages/product-detail/index.style'
import DisplayPrice from '../display-price'
import {MinusIcon, AddIcon} from '@chakra-ui/icons'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {Img, Tooltip, useBreakpointValue} from '@chakra-ui/react'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import CartErrorMessage from '../CartErrorMessage'
import {
    buttonContainer,
    calloutBoxStyle,
    containerStyle,
    desktopDetailsStyle,
    flexStyle,
    hStackStyle,
    iconStyle,
    inputStyle,
    minusButtonStyle,
    mobileDetailsStyle,
    plusButtonStyle,
    stockLeftStyle,
    tagIconStyle,
    tooltipStyle,
    colorSwatchStyle,
    promoContentStyle,
    giftCardLinkStyle
} from './index.style'
import CloudinaryImageGallery from '../../cloudinary/cloudinary-image-gallery'
import {galleryOptions} from '../../static/constants'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {pxToRem} from '../../utills/pxToRem'
import useSitePreference from '../../hooks/use-site-preference'
import useUpdateShopperContext from '../store-selector-modal/updateShopperContext'
import {rebuildPathWithParams} from '@salesforce/retail-react-app/app/utils/url'
import DeliveryAddress from '../delivery-address'
import useSizeMappingData from '../../utills/getProductSizeMapping'
import CloudinaryImageSwatches from '../../cloudinary/cloudinary-image-swatches'
import useCustomFetch from '../../hooks/useFetchContent'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getMarketingProductAttributes} from '../../utills/gtm/getMarketingProductAttributes'

const ProductViewHeader = ({name, currency, priceData, product, isProductPartOfBundle}) => {
    const [savingsCalc, setSavingsCalc] = useState()
    const intl = useIntl()
    const bazaarvoiceProductId = product?.master?.masterId || product?.id

    useEffect(() => {
        const saving =
            Math.floor(
                ((priceData?.listPrice - priceData?.currentPrice) / priceData?.listPrice) * 100
            ).toFixed() + '%'
        setSavingsCalc(saving)
    }, [priceData?.currentPrice, priceData?.listPrice])

    useEffect(() => {
        if (!bazaarvoiceProductId) {
            return
        }
        window.$BV?.ui?.refresh?.()
    }, [bazaarvoiceProductId])

    return (
        <VStack mr={4} spacing={2} align="flex-start" marginBottom={[4, 4, 4, 0, 0]}>
            <Skeleton isLoaded={name}>
                <Heading sx={titleStyle}>{`${name}`}</Heading>
            </Skeleton>

            {!isProductPartOfBundle && (
                <Skeleton sx={priceStyle} isLoaded={priceData?.currentPrice}>
                    {priceData?.currentPrice && (
                        <>
                            <DisplayPrice
                                priceData={priceData}
                                isPriceRange={product?.c_isPriceRange}
                                currency={currency}
                                currentPriceProps={
                                    priceData.isOnSale
                                        ? {...priceStyle, ...salePriceStyle}
                                        : {...priceStyle}
                                }
                                listPriceProps={priceStyle}
                            />
                            {savingsCalc !== '0%' && savingsCalc && !product?.c_isPriceRange && (
                                <Text sx={savingPriceStyle}>
                                    {savingsCalc}{' '}
                                    {intl.formatMessage({
                                        id: 'savings_percentage',
                                        defaultMessage: ` Savings`
                                    })}
                                </Text>
                            )}
                        </>
                    )}
                </Skeleton>
            )}

            {bazaarvoiceProductId && (
                <Box
                    data-bv-show="rating_summary"
                    data-bv-product-id={bazaarvoiceProductId}
                    width="100%"
                />
            )}
        </VStack>
    )
}

ProductViewHeader.propTypes = {
    name: PropTypes.string,
    currency: PropTypes.string,
    category: PropTypes.array,
    priceData: PropTypes.object,
    product: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool,
    isProductPartOfSet: PropTypes.bool
}

const QuantityPickerSection = ({
    quantity,
    setSelectedQuantity,
    minOrderQuantity,
    stockLevel,
    setQuantity,
    stepQuantity,
    isVisible = false
}) => {
    const maxAllowed = Math.min(stockLevel, 10)

    const dec = () =>
        setQuantity((q) => (q - stepQuantity >= minOrderQuantity ? q - stepQuantity : q))

    const inc = () => setQuantity((q) => (q + stepQuantity <= maxAllowed ? q + stepQuantity : q))

    const plusDisabled = quantity + stepQuantity > maxAllowed
    const minusDisabled = quantity - stepQuantity < minOrderQuantity
    setSelectedQuantity(quantity)

    return (
        isVisible && (
            <Flex sx={containerStyle}>
                <Button sx={minusButtonStyle} onClick={dec} isDisabled={minusDisabled}>
                    <MinusIcon sx={iconStyle} />
                </Button>
                <Text sx={inputStyle} as="span">
                    {quantity}
                </Text>
                <Button sx={plusButtonStyle} onClick={inc} isDisabled={plusDisabled}>
                    <AddIcon sx={iconStyle} />
                </Button>
            </Flex>
        )
    )
}

QuantityPickerSection.propTypes = {
    quantity: PropTypes.number,
    minOrderQuantity: PropTypes.number,
    stockLevel: PropTypes.number,
    setQuantity: PropTypes.func,
    stepQuantity: PropTypes.number,
    isVisible: PropTypes.bool,
    setSelectedQuantity: PropTypes.func,
    product: PropTypes.object
}

const LowStockMessage = ({stockLevel}) => {
    const inStockValue = useSitePreference('inStockValue')
    const intl = useIntl()

    if (stockLevel > 0 && stockLevel < inStockValue?.data?.inStockValue) {
        return (
            <Text sx={stockLeftStyle} as="span">
                {intl.formatMessage(
                    {
                        id: 'pdp.quantity.details.lowstock',
                        defaultMessage: 'In Stock – Only {stockLevel} left in stock'
                    },
                    {
                        stockLevel: stockLevel
                    }
                )}
            </Text>
        )
    }
    return null
}

LowStockMessage.propTypes = {
    stockLevel: PropTypes.number
}

const PromotionsSection = ({promos = [], product}) => {
    const intl = useIntl()
    const isMobile = useBreakpointValue({base: true, md: false})
    const [openTooltipId, setOpenTooltipId] = useState(null)
    const toggleTooltip = (id) => {
        setOpenTooltipId((prev) => (prev === id ? null : id))
    }

    return (
        <VStack align="start" spacing={2} mt={4} width="100%">
            {promos.map(({id, details, calloutMessage}) => (
                <Flex key={id} sx={flexStyle}>
                    {calloutMessage && (
                        <HStack sx={hStackStyle}>
                            {product?.c_productPromotionsDetails && (
                                <Box
                                    backgroundImage={`url(${getAssetUrl(
                                        'static/img/pdp/tagIcon.svg'
                                    )})`}
                                    sx={tagIconStyle}
                                />
                            )}
                            <Box sx={calloutBoxStyle}>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: calloutMessage
                                    }}
                                />
                                {details && (
                                    <Tooltip label={details} sx={tooltipStyle}>
                                        <Text sx={desktopDetailsStyle} as="span">
                                            {intl.formatMessage({
                                                id: 'pdp.promotions.details.details',
                                                defaultMessage: 'DETAILS'
                                            })}
                                        </Text>
                                    </Tooltip>
                                )}
                            </Box>
                        </HStack>
                    )}
                    {isMobile && details && (
                        <Tooltip label={details} sx={tooltipStyle} trigger="hover">
                            <Text
                                sx={mobileDetailsStyle}
                                as="span"
                                tabIndex={0}
                                role="button"
                                aria-describedby={`tooltip-${id}`}
                                onClick={() => toggleTooltip(id)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        toggleTooltip(id)
                                    }
                                }}
                            >
                                {intl.formatMessage({
                                    id: 'pdp.promotions.details.details',
                                    defaultMessage: 'DETAILS'
                                })}
                            </Text>
                        </Tooltip>
                    )}
                </Flex>
            ))}
        </VStack>
    )
}

PromotionsSection.propTypes = {
    promos: PropTypes.array,
    product: PropTypes.object
}

const ProductView = forwardRef(
    (
        {
            product,
            setSelectedQuantity = () => {},
            category,
            showFullLink = false,
            imageSize = 'md',
            isWishlistLoading = false,
            addToCart,
            updateCart,
            addToWishlist,
            updateWishlist,
            isItemWishlisted,
            wishlist,
            isProductLoading,
            isProductPartOfSet = false,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            childProductOrderability,
            setChildProductOrderability,
            isBasketLoading = false,
            onVariantSelected = () => {},
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && variant?.orderable && quantity > 0 && quantity <= stockLevel,
            showImageGallery = true,
            setSelectedBundleQuantity = () => {},
            selectedBundleParentQuantity = 1,
            updateDeliceryType,
            pdpVariationCategory = [],
            setIsPickUpInStore,
            addToCartError,
            isCartAtLimit = false
        },
        ref
    ) => {
        const {currency: activeCurrency} = useCurrency()
        const showToast = useToast()
        const intl = useIntl()
        const location = useLocation()
        const hasFired = useRef(false)
        const {
            isOpen: isAddToCartModalOpen,
            onOpen: onAddToCartModalOpen,
            onClose: onAddToCartModalClose
            // isItemAddedToCart
            // setIsItemAddedToCart
        } = useAddToCartModalContext()
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(true)
        const {
            showLoading,
            showInventoryMessage,
            inventoryMessage,
            quantity,
            minOrderQuantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel,
            stepQuantity,
            isOutOfStock,
            isMaster,
            unfulfillable
        } = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle)
        const hasOrderableVariants = product?.variants?.some((v) => v?.orderable)
        const isProductEffectivelyAvailable =
            product?.c_isProductAvailable || (isMaster !== undefined && hasOrderableVariants)
        const priceData = useMemo(() => {
            return getPriceData(product, {quantity})
        }, [product, quantity])
        const canAddToWishlist = !isProductLoading
        const isProductASet = product?.type?.set
        const isProductABundle = product?.type?.bundle
        const contentAssetIDs = 'pdp-loyalty-message'
        const errorContainerRef = useRef(null)
        const {data: customer} = useCurrentCustomer()
        const {shopperContext} = useUpdateShopperContext()
        const [storePickUp, setStorePickUp] = useState('shipToMe')
        const sizeMappingData = useSizeMappingData()
        const {token} = useAccessToken()
        const {app: appConfig} = getConfig()
        const {response} = useCustomFetch(token, contentAssetIDs, false)

        const storeDetails = useMemo(() => {
            if (shopperContext?.customQualifiers?.storeDetails) {
                return JSON.parse(shopperContext?.customQualifiers?.storeDetails)
            }
            return {}
        }, [shopperContext])

        const {disableButton} = useMemo(() => {
            let shouldDisableButton = showInventoryMessage
            let currentInventoryMsg = ''
            if (
                !shouldDisableButton &&
                (isProductASet || isProductABundle) &&
                childProductOrderability
            ) {
                // if any of the children are not orderable, it will disable the add to cart button
                const unavailableChildProductKey = Object.keys(childProductOrderability)?.find(
                    (key) => {
                        return childProductOrderability[key].showInventoryMessage
                    }
                )
                shouldDisableButton = !!unavailableChildProductKey
                if (unavailableChildProductKey) {
                    const unavailableChildProduct =
                        childProductOrderability[unavailableChildProductKey]
                    if (unavailableChildProduct.unfulfillable) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Only {stockLevel} left for {productName}!',
                                id: 'use_product.message.inventory_remaining_for_product'
                            },
                            {
                                stockLevel: unavailableChildProduct.stockLevel,
                                productName: unavailableChildProduct.productName
                            }
                        )
                    }
                    // if (unavailableChildProduct.isOutOfStock) {
                    //     currentInventoryMsg = intl.formatMessage(
                    //         {
                    //             defaultMessage: 'Out of stock for {productName}',
                    //             id: 'use_product.message.out_of_stock_for_product'
                    //         },
                    //         {productName: unavailableChildProduct.productName}
                    //     )
                    // }
                }
            }
            return {disableButton: shouldDisableButton, customInventoryMessage: currentInventoryMsg}
        }, [showInventoryMessage, childProductOrderability])

        const validateAndShowError = (opts = {}) => {
            const {scrollErrorIntoView = true} = opts
            // Validate that all attributes are selected before proceeding.
            const hasValidSelection = validateOrderability(variant, quantity, stockLevel)
            const showError = !isProductASet && !isProductABundle && !hasValidSelection
            const scrollToError = showError && scrollErrorIntoView

            toggleShowOptionsMessage(showError)

            if (scrollToError) {
                errorContainerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
            }

            return hasValidSelection
        }

        const renderActionButtons = (isItemWishlisted) => {
            const buttons = []
            const buttonText = {
                update: intl.formatMessage({
                    defaultMessage: 'Update',
                    id: 'product_view.button.update'
                }),
                addToCart: intl.formatMessage({
                    defaultMessage: 'Add to Cart',
                    id: 'product_view.button.add_to_cart'
                }),
                addSetToCart: intl.formatMessage({
                    defaultMessage: 'Add Set to Cart',
                    id: 'product_view.button.add_set_to_cart'
                }),
                addBundleToCart: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Cart',
                    id: 'product_view.button.add_bundle_to_cart'
                }),
                addToWishlist: intl.formatMessage({
                    defaultMessage: 'Add to Wishlist',
                    id: 'product_view.button.add_to_wishlist'
                }),
                addSetToWishlist: intl.formatMessage({
                    defaultMessage: 'Add Set to Wishlist',
                    id: 'product_view.button.add_set_to_wishlist'
                }),
                addBundleToWishlist: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Wishlist',
                    id: 'product_view.button.add_bundle_to_wishlist'
                })
            }

            const showError = () => {
                showToast({
                    title: intl.formatMessage(API_ERROR_MESSAGE),
                    status: 'error'
                })
            }

            const handleCartItem = async () => {
                const hasValidSelection = validateAndShowError()
                const c_storeId = storeDetails?.id
                const c_storepickup = storePickUp === 'shipToMe' ? false : true

                if (!hasValidSelection) {
                    return null
                }

                if (!addToCart && !updateCart) return null
                if (updateCart) {
                    await updateCart(variant || product, quantity, c_storeId, c_storepickup)
                    return
                }
                try {
                    const itemsAdded = await addToCart(variant, quantity, c_storeId, c_storepickup)
                    // Open modal only when `addToCart` returns some data
                    // It's possible that the item has been added to cart, but we don't want to open the modal.
                    // See wishlist_primary_action for example.
                    if (itemsAdded) {
                        onAddToCartModalOpen({
                            product,
                            itemsAdded,
                            selectedQuantity: quantity
                        })
                    }
                } catch (e) {
                    showError()
                }
            }

            const handleWishlistItem = async () => {
                //these changes are added to hybrid launch
                if (!customer.isRegistered) {
                    const queryParams = Object.fromEntries(
                        new URLSearchParams(location.search).entries()
                    )
                    const url = new URL(product.slugUrl, window.location.origin)
                    const relativePath = url.pathname.replace('/s/Cavenders', '')
                    const updatedUrl = rebuildPathWithParams(relativePath, queryParams)
                    const pdpUrlParam = `/?original=${encodeURIComponent(updatedUrl)}`

                    window.location.href = `/login${pdpUrlParam}`
                } else {
                    if (!updateWishlist && !addToWishlist) return null
                    if (updateWishlist) {
                        updateWishlist(product, variant, quantity)
                        return
                    }
                    addToWishlist(product, variant, quantity)
                }
            }

            // child product of bundles do not have add to wishlist button
            if ((addToWishlist || updateWishlist) && !isProductPartOfBundle) {
                buttons.push(
                    <Tooltip
                        sx={wishlistTooltip}
                        label={
                            isItemWishlisted
                                ? intl.formatMessage({defaultMessage: 'Already in Wishlist'})
                                : !showOptionsMessage
                                ? customer.isRegistered
                                    ? intl.formatMessage({
                                          defaultMessage: 'Add to Wishlist'
                                      })
                                    : intl.formatMessage({
                                          defaultMessage: 'Must be signed in to add to Wishlist'
                                      })
                                : intl.formatMessage({
                                      defaultMessage: 'Select Size to Add to Wishlist'
                                  })
                        }
                        placement="bottom-start"
                        isDisabled={
                            isItemWishlisted || !showOptionsMessage
                                ? customer.isRegistered
                                    ? false
                                    : false
                                : false
                        }
                    >
                        <span>
                            <Button
                                sx={wishlistButton}
                                key="wishlist-button"
                                onClick={handleWishlistItem}
                                isDisabled={
                                    isWishlistLoading ||
                                    !canAddToWishlist ||
                                    showOptionsMessage ||
                                    !isProductEffectivelyAvailable
                                }
                                isLoading={isWishlistLoading}
                                variant="outline"
                            >
                                {isItemWishlisted ? (
                                    <Img
                                        sx={wishlistIcon}
                                        src={getAssetUrl('static/img/pdp/wishlistFilledIcon.png')}
                                    />
                                ) : (
                                    <Img
                                        sx={wishlistIcon}
                                        src={getAssetUrl('static/img/pdp/heartEmpty.png')}
                                    />
                                )}
                            </Button>
                        </span>
                    </Tooltip>
                )
            }

            // child product of bundles do not have add to cart button
            if ((addToCart || updateCart) && !isProductPartOfBundle) {
                buttons.push(
                    <Button
                        sx={addToCartButton}
                        key="cart-button"
                        onClick={handleCartItem}
                        isDisabled={
                            disableButton ||
                            showOptionsMessage ||
                            !isProductEffectivelyAvailable ||
                            isCartAtLimit
                        }
                        isLoading={isBasketLoading}
                        variant="solid"
                    >
                        {updateCart
                            ? buttonText.update
                            : isProductASet
                            ? buttonText.addSetToCart
                            : isProductABundle
                            ? buttonText.addBundleToCart
                            : buttonText.addToCart}
                    </Button>
                )
            }

            return buttons
        }

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        // Other values can be added to this scope as required.
        if (typeof ref === 'function') {
            ref = ref.bind({validateOrderability: validateAndShowError})
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        useEffect(() => {
            if (isAddToCartModalOpen) {
                onAddToCartModalClose()
            }
        }, [location.pathname])

        useEffect(() => {
            if (
                !isProductASet &&
                !isProductABundle &&
                validateOrderability(variant, quantity, stockLevel)
            ) {
                toggleShowOptionsMessage(false)
            }
        }, [variationParams])

        useEffect(() => {
            if (variant) {
                onVariantSelected(product, variant, quantity)
            }
        }, [variant?.productId, quantity])

        useEffect(() => {
            if (isProductPartOfBundle || isProductPartOfSet) {
                const key = product.itemId ?? product.id
                // when showInventoryMessage is true, it means child product is not orderable
                setChildProductOrderability((previousState) => ({
                    ...previousState,
                    [key]: {
                        showInventoryMessage,
                        isOutOfStock,
                        unfulfillable,
                        stockLevel,
                        productName: product?.name
                    }
                }))
            }
        }, [showInventoryMessage, inventoryMessage])

        useEffect(() => {
            if (variant?.productId) {
                setQuantity(minOrderQuantity)
                if (isProductABundle) setSelectedBundleQuantity(minOrderQuantity)
            }
        }, [variant?.productId])

        useEffect(() => {
            if (variationAttributes?.length) {
                let flag = false
                variationAttributes?.forEach((attr) => {
                    if (!attr?.selectedValue?.value) {
                        flag = true
                    }
                })
                toggleShowOptionsMessage(flag)
            }
        }, [variationAttributes])

        useEffect(() => {
            if (product?.type?.variant && !product?.c_instorePickupOnPWA?.enableInStorePickup) {
                setStorePickUp('shipToMe')
                setIsPickUpInStore && setIsPickUpInStore(false)
            }
        }, [product])

        // analytics
        const [gtmRetryTick, setGtmRetryTick] = useState(0)
        useEffect(() => {
            if (hasFired.current) return

            const currentProductId = product?.master?.masterId
            if (!currentProductId) return

            const expectsCategory = !!(product?.primaryCategoryId || product?.c_primaryCatergoryID)
            const isDataReady =
                category && (!expectsCategory || category.length > 0) && product && priceData
            if (!isDataReady) return

            if (!window?.dataLayer || !Array.isArray(window.dataLayer)) {
                const retryTimer = setTimeout(() => setGtmRetryTick((tick) => tick + 1), 200)
                return () => clearTimeout(retryTimer)
            }

            hasFired.current = true // prevent future runs
            // analytics: Page View Context
            window.dataLayer.push({
                'page-type': 'product detail',
                google_tag_params: {
                    ecomm_pagetype: 'product',
                    ecomm_prodid: product?.master?.masterId,
                    ecomm_totalvalue: priceData?.currentPrice
                }
            })
            window.dataLayer.push({
                pageType: 'product_group',
                productID: product?.master?.masterId,
                price: priceData?.currentPrice
            })

            // analytics: Product Detail View
            window.dataLayer.push({
                // Push product detail view
                ecommerce: {
                    detail: {
                        products: [
                            {
                                name: product?.name,
                                id: product?.master?.masterId,
                                brand: product?.brand,
                                category: category?.map((cat) => cat?.name)?.join('/'),
                                price: priceData?.currentPrice,
                                variant: null,
                                ...getMarketingProductAttributes(product)
                            }
                        ]
                    }
                },
                event: 'productDetailView'
            })
        }, [category, gtmRetryTick, hasFired.current])
        useEffect(() => {
            if (product?.master?.masterId) {
                hasFired.current = false
            } else {
                hasFired.current = true
            }
        }, [product?.master?.masterId])

        return (
            <Flex
                direction={'column'}
                maxWidth={['none', 'none', 1440, 1440]}
                data-testid="product-view"
                ref={ref}
            >
                {/* Basic information etc. title, price, breadcrumb*/}
                <Box flex="0 0 40%" display={['block', 'block', 'block', 'none']}>
                    <ProductViewHeader
                        name={product?.name}
                        product={product}
                        priceData={priceData}
                        currency={product?.currency || activeCurrency}
                        category={category}
                        isProductPartOfBundle={isProductPartOfBundle}
                    />
                </Box>
                <Flex direction={['column', 'column', 'column', 'row']}>
                    {showImageGallery && (
                        <Box flex="0 0 60%" mr={[0, 0, 0, 6, 6]} sx={hideAfterEighthImgs}>
                            {product ? (
                                <>
                                    {product?.c_cloudinary?.pdp ? (
                                        <CloudinaryImageGallery
                                            size={imageSize}
                                            cloudinaryImageGallery={product.c_cloudinary}
                                            selectedVariationAttributes={variationParams}
                                            isProductSetOrBundle={isProductASet || isProductABundle}
                                            galleryOptionsDetail={galleryOptions}
                                        />
                                    ) : (
                                        <>
                                            <ImageGallery
                                                size={imageSize}
                                                imageGroups={product.imageGroups}
                                                selectedVariationAttributes={variationParams}
                                                lazy={isProductPartOfSet || isProductPartOfBundle}
                                            />
                                            {/* <HideOnMobile>
                                                {showFullLink && product && (
                                                    <Link
                                                        to={`/product/${product.master.masterId}`}
                                                        color="blue.600"
                                                    >
                                                        <FormattedMessage
                                                            id="product_view.link.full_details"
                                                            defaultMessage="See full details"
                                                        />
                                                    </Link>
                                                )}
                                            </HideOnMobile> */}
                                        </>
                                    )}
                                </>
                            ) : (
                                <ImageGallerySkeleton />
                            )}
                        </Box>
                    )}

                    {/* Variations & Quantity Selector & CTA buttons */}
                    <VStack align="stretch" spacing={8} flex={1}>
                        <Box display={['none', 'none', 'none', 'block']}>
                            <ProductViewHeader
                                name={product?.name}
                                product={product}
                                priceData={priceData}
                                currency={product?.currency || activeCurrency}
                                category={category}
                                isProductPartOfBundle={isProductPartOfBundle}
                            />
                        </Box>
                        <VStack align="stretch" spacing={4}>
                            {isProductPartOfBundle && (
                                <Box>
                                    <Text fontWeight="medium" fontSize="md" aria-label="price">
                                        <label>
                                            {intl.formatMessage({
                                                defaultMessage: 'Quantity',
                                                id: 'product_view.label.quantity'
                                            })}
                                            : {childOfBundleQuantity}
                                        </label>
                                    </Text>
                                </Box>
                            )}

                            {showLoading ? (
                                <>
                                    <Skeleton height={6} width={32} />
                                    <Skeleton height={20} width={64} />

                                    <Skeleton height={6} width={32} />
                                    <Skeleton height={20} width={64} />
                                </>
                            ) : (
                                variationAttributes?.map(({id, name, selectedValue, values}) => {
                                    const variationName = pdpVariationCategory?.includes(
                                        product?.primaryCategoryId || product?.c_primaryCatergoryID
                                    )
                                        ? name === 'Size'
                                            ? intl.formatMessage({
                                                  id: 'pdp.variation.waist.label',
                                                  defaultMessage: 'Waist'
                                              })
                                            : name === 'Width'
                                            ? intl.formatMessage({
                                                  id: 'pdp.variation.length.label',
                                                  defaultMessage: 'Length'
                                              })
                                            : name
                                        : name

                                    const swatches = values?.map(
                                        ({href, name: swatchName, value, orderable}, index) => {
                                            const content =
                                                sizeMappingData[swatchName] || swatchName
                                            const hasSelection = Boolean(selectedValue?.value)
                                            const isSelected = selectedValue?.value === value
                                            const isFirst = index === 0
                                            const isFocusable =
                                                isSelected || (!hasSelection && isFirst)

                                            const hasOrderableVariant =
                                                id === 'color' ||
                                                product?.variants?.some(
                                                    (variant) =>
                                                        variant?.variationValues[id] === value &&
                                                        variant?.orderable
                                                )

                                            return (
                                                <Swatch
                                                    key={value}
                                                    href={href}
                                                    disabled={!orderable || !hasOrderableVariant}
                                                    value={value}
                                                    name={swatchName}
                                                    variantName={variationName}
                                                    variant={id === 'color' ? 'circle' : 'square'}
                                                    selected={isSelected}
                                                    isFocusable={isFocusable}
                                                >
                                                    {content}
                                                </Swatch>
                                            )
                                        }
                                    )
                                    return (
                                        <>
                                            {id === 'color' ? (
                                                values.length > 0 &&
                                                values.map(({name: swatchName, value}) => (
                                                    <Box key={value}>
                                                        <Text sx={colorSwatchStyle}>
                                                            <span>
                                                                {intl.formatMessage({
                                                                    defaultMessage: 'Color:',
                                                                    id: 'product_view.label.color'
                                                                })}
                                                            </span>{' '}
                                                            <span>
                                                                {swatchName &&
                                                                typeof swatchName === 'string'
                                                                    ? swatchName
                                                                          .split(' ')
                                                                          .map(
                                                                              (word) =>
                                                                                  word
                                                                                      .charAt(0)
                                                                                      .toUpperCase() +
                                                                                  word
                                                                                      .slice(1)
                                                                                      .toLowerCase()
                                                                          )
                                                                          .join(' ')
                                                                    : swatchName}
                                                            </span>
                                                        </Text>
                                                        <CloudinaryImageSwatches
                                                            cloudinaryImageGallery={
                                                                product?.c_cloudinary
                                                                    ? product.c_cloudinary
                                                                    : null
                                                            }
                                                            value={value}
                                                            showFallbackImage={true}
                                                        />
                                                    </Box>
                                                ))
                                            ) : (
                                                <SwatchGroup
                                                    key={id}
                                                    c_sizeChart={product?.c_sizeChart}
                                                    value={selectedValue?.value}
                                                    displayName={selectedValue?.name || ''}
                                                    label={intl.formatMessage(
                                                        {
                                                            defaultMessage: '{variantType}',
                                                            id: 'product_view.label.variant_type'
                                                        },
                                                        {variantType: variationName}
                                                    )}
                                                >
                                                    {swatches}
                                                </SwatchGroup>
                                            )}
                                        </>
                                    )
                                })
                            )}

                            {!isProductASet && !isProductPartOfBundle && (
                                <HStack align="stretch" marginTop="0">
                                    {product?.c_isProductAvailable == undefined || isMaster ? (
                                        <></>
                                    ) : isProductEffectivelyAvailable ? (
                                        <LowStockMessage stockLevel={stockLevel} />
                                    ) : (
                                        <Text sx={stockLeftStyle}>
                                            {intl.formatMessage({
                                                id: 'pdp.quantity.details.outofstock',
                                                defaultMessage: 'CURRENTLY OUT OF STOCK'
                                            })}
                                        </Text>
                                    )}
                                </HStack>
                            )}
                            <DeliveryAddress
                                product={product}
                                quantity={quantity}
                                updateDeliceryType={updateDeliceryType}
                                storePickUp={storePickUp}
                                setStorePickUp={setStorePickUp}
                                marginTop={0}
                                setQuantity={setQuantity}
                                minOrderQuantity={minOrderQuantity}
                            />

                            {!isProductASet && !isProductPartOfBundle && (
                                <HStack sx={buttonContainer}>
                                    <Box sx={renderButtons}>
                                        {renderActionButtons(isItemWishlisted)}
                                        <QuantityPickerSection
                                            quantity={quantity}
                                            minOrderQuantity={minOrderQuantity}
                                            stockLevel={stockLevel}
                                            setQuantity={setQuantity}
                                            setSelectedQuantity={setSelectedQuantity}
                                            stepQuantity={stepQuantity}
                                            isVisible={isProductEffectivelyAvailable}
                                        />
                                    </Box>
                                </HStack>
                            )}

                            {addToCartError && (
                                <HStack>
                                    <CartErrorMessage message={addToCartError} />
                                </HStack>
                            )}
                            {(product?.primaryCategoryId || product?.c_primaryCatergoryID) ===
                                'gift-cards' && (
                                <Link
                                    to={`/cavenders-gift-cards/?checkbalance`}
                                    sx={giftCardLinkStyle}
                                >
                                    <FormattedMessage
                                        id="product_view.link.gift_card_balance"
                                        defaultMessage="check your gift card balance >>"
                                    />
                                </Link>
                            )}
                            {appConfig.afterPay.enableMessaging &&
                                appConfig.afterPay.productMPID &&
                                appConfig.afterPay.productPlacementID &&
                                !isProductASet &&
                                !isProductPartOfBundle && (
                                    <square-placement
                                        data-mpid={appConfig.afterPay.productMPID}
                                        data-placement-id={appConfig.afterPay.productPlacementID}
                                        data-page-type="product"
                                        data-amount={priceData.currentPrice * quantity}
                                        data-currency={product?.currency || activeCurrency}
                                        data-consumer-locale="en_US"
                                        data-item-skus={product?.itemId ?? product?.id}
                                        data-is-eligible="true"
                                    ></square-placement>
                                )}

                            {!isProductASet && !isProductPartOfBundle && (
                                <Box
                                    sx={promoContentStyle}
                                    dangerouslySetInnerHTML={{__html: response}}
                                ></Box>
                            )}

                            {!isProductASet && !isProductPartOfBundle && (
                                <HStack align="stretch">
                                    <PromotionsSection
                                        product={product}
                                        promos={product?.c_productPromotionsDetails}
                                    />
                                </HStack>
                            )}

                            <HideOnDesktop>
                                {showFullLink && product && (
                                    <Link
                                        to={`/product/${product.master.masterId}`}
                                        color="blue.600"
                                    >
                                        <FormattedMessage
                                            id="product_view.link.full_details"
                                            defaultMessage="See full details"
                                        />
                                    </Link>
                                )}
                            </HideOnDesktop>
                            {isProductASet && <p>{product?.shortDescription}</p>}
                        </VStack>

                        <InformationAccordion product={product} />
                    </VStack>
                </Flex>
            </Flex>
        )
    }
)

ProductView.displayName = 'ProductView'

ProductView.propTypes = {
    product: PropTypes.object,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    childOfBundleQuantity: PropTypes.number,
    category: PropTypes.array,
    isProductLoading: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    addToCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    isItemWishlisted: PropTypes.bool,
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    showFullLink: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    childProductOrderability: PropTypes.object,
    setChildProductOrderability: PropTypes.func,
    onVariantSelected: PropTypes.func,
    validateOrderability: PropTypes.func,
    showImageGallery: PropTypes.bool,
    setSelectedBundleQuantity: PropTypes.func,
    selectedBundleParentQuantity: PropTypes.number,
    selectedBundleQuantity: PropTypes.number,
    setSelectedQuantity: PropTypes.func,
    wishlist: PropTypes.object,
    updateDeliceryType: PropTypes.func,
    pdpVariationCategory: PropTypes.array,
    setIsPickUpInStore: PropTypes.func,
    addToCartError: PropTypes.string,
    isCartAtLimit: PropTypes.bool
}

export default ProductView
